import React, { useMemo, useState } from "react";

import { useCustomerBreakdownConfigs } from "@components/data/customer-breakdown-configs";
import { useUser } from "@components/user-context";
import { METRIC_OPTIONS } from "@features/customer-ltv-v2/constants";
import { useCustomerLtvV2 } from "@features/customer-ltv-v2/hooks";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";
import { ROLLUP_MODES } from "@pages/customers/report";
import { SumModeSelect } from "@pages/customers/report/sum-mode-select";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { SearchInput, type SearchValue } from "@shared/search-input";
import { Select } from "@shared/selects";

import { DataExportModal } from "./data-export-modal";

export const Toolbar = () => {
  const { user } = useUser();
  const { value: breakdownConfigs } = useCustomerBreakdownConfigs();
  const { filters, loading, setFilters } = useCustomerLtvV2();
  const [searchValue, setSearchValue] = useState<SearchValue>(filters.search);

  const search = () => {
    prettyLogEvent.onChange({
      user,
      page: Pages.CUSTOMERS,
      additionalActionInfo: "change-search",
    });

    setFilters({ search: searchValue, page: 1 });
  };

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const breakdownLabel = useMemo(() => {
    const selectedBreakdown = breakdownConfigs?.find(
      (e) => e.key === filters.breakdown?.key,
    );

    return selectedBreakdown?.name;
  }, [filters.breakdown, breakdownConfigs]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      search();
    }
  };

  return (
    <div className="flex items-end justify-between">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Select
            className="w-[240px]"
            label="METRIC"
            isDisabled={loading}
            value={filters.metric}
            options={Object.entries(METRIC_OPTIONS).map(([value, label]) => ({
              label,
              value,
            }))}
            onChange={(value) => {
              prettyLogEvent.onSelect({
                user,
                page: Pages.CUSTOMERS,
                additionalActionInfo: "select-metric",
              });

              setFilters({ metric: value });
            }}
          />
          <Select
            className="w-[220px]"
            label="ROLLUP MODE"
            isDisabled={loading}
            value={filters.rollupMode}
            options={ROLLUP_MODES.map((value) => ({ value, label: value }))}
            onChange={(value) => {
              prettyLogEvent.onSelect({
                user,
                page: Pages.CUSTOMERS,
                additionalActionInfo: "select-rollup-mode",
              });

              setFilters({ rollupMode: value });
            }}
          />
          <SumModeSelect
            className="w-[270px]"
            isDisabled={loading}
            isLoading={false}
            sumMode={filters.sumMode}
            setSumMode={(value) => {
              prettyLogEvent.onSelect({
                user,
                page: Pages.CUSTOMERS,
                additionalActionInfo: "select-sum-mode",
              });

              setFilters({ sumMode: value });
            }}
          />
        </div>
        <div className="flex items-end space-x-2">
          <SearchInput
            className="w-[400px]"
            label="SEARCH"
            placeholder={
              breakdownLabel
                ? `Search ${breakdownLabel?.toLowerCase()}...`
                : "Search..."
            }
            isDisabled={loading}
            value={searchValue}
            onChange={setSearchValue}
            onKeyDown={handleKeyDown}
          />
          <PrimaryButton isDisabled={loading} onClick={search}>
            Search
          </PrimaryButton>
        </div>
      </div>
      <SecondaryButton
        disabled={loading}
        className="font-bold self-center"
        onClick={() => setIsExportModalOpen(true)}
      >
        EXPORT TO CSV
      </SecondaryButton>
      <DataExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
    </div>
  );
};
